import classNames from "classnames";
import {
  ChangeEvent,
  type KeyboardEvent,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
  MutableRefObject,
} from "react";
import { EventOutside } from "../../Dropdown/DropdownBody";
import { defaultPageSettings, TreeContext } from "./CustomDropdown";
import {
  getDropdownBodyHeight,
  getDropdownCoords,
  getDropdownWidth,
} from "../../../../core/helpers/dropdownHelper";
import { Coords } from "../../../../core/interfaces/Coords";
import { LiItem } from "./LiItem";
import TextInput from "../../Inputs/TextInput";
import Icon from "../../Icon";
import LoadingSpinner from "../../Loading/LoadingSpinner";
import useTranslations from "../../../../core/i18n/useTranslations";

import styles from "./CustomTreeDropdown.module.css";

export const CustomTree = ({
  onClose,
  parentRef,
  searchValueRef,
  pageSettingsRef,
  itemsToShow,
}: {
  onClose: () => void;
  parentRef: RefObject<any>;
  searchValueRef: MutableRefObject<string>;
  pageSettingsRef: MutableRefObject<{
    page: number;
    pageSize: number;
  }>;
  itemsToShow: number;
}) => {
  const t = useTranslations();
  const bodyWidth: number | string = getDropdownWidth(parentRef.current);
  const { items, fetchOrganizations, isLoading, canFetchMore } =
    useContext(TreeContext);
  const bodyHeight = getDropdownBodyHeight(
    itemsToShow,
    window.innerHeight <= 750 ? 6 : itemsToShow,
    true
  );
  const coords: Coords = getDropdownCoords(parentRef.current, bodyHeight);
  const [localSearchValue, setLocalSearchValue] = useState(
    searchValueRef.current
  );
  const [needsRefetch, setNeedsRefetch] = useState(false);
  const resultWrapperRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const canFetchMoreRef = useRef(canFetchMore);
  const isLoadingRef = useRef(false);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const handleTabOnDocument = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Tab" || !portalRef.current) return;
      if (!portalRef.current.contains(e.target as Node)) return;

      onCloseRef.current();
      parentRef.current?.focus();

      if (e.defaultPrevented) {
        parentRef.current?.dispatchEvent(
          new KeyboardEvent("keydown", {
            key: "Tab",
            code: "Tab",
            shiftKey: e.shiftKey,
            bubbles: true,
            cancelable: true,
          })
        );
      }
    };

    document.addEventListener("keydown", handleTabOnDocument, true);
    return () =>
      document.removeEventListener("keydown", handleTabOnDocument, true);
  }, []);

  useEffect(() => {
    canFetchMoreRef.current = canFetchMore;
  }, [canFetchMore]);

  useEffect(() => {
    if (needsRefetch) {
      fetchOrganizations();
      setNeedsRefetch(false);
    }
  }, [needsRefetch]);

  const handleScroll = () => {
    if (resultWrapperRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        resultWrapperRef.current;
      if (Math.ceil(scrollTop + clientHeight) >= scrollHeight) {
        if (canFetchMoreRef.current && !isLoadingRef.current) {
          isLoadingRef.current = true;
          fetchOrganizations().finally(() => (isLoadingRef.current = false));
        }
      }
    }
  };

  useEffect(() => {
    const resultWrapper = resultWrapperRef.current;
    if (resultWrapper) {
      resultWrapper.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (resultWrapper) {
        resultWrapper.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const debouncedSetSearchValue = (value: string) => {
    searchValueRef.current = value;
    pageSettingsRef.current = { ...defaultPageSettings };
    setNeedsRefetch(true);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchValue(value);
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }
    intervalRef.current = setTimeout(() => {
      debouncedSetSearchValue(value);
    }, 500);
  };

  const getFocusableItems = (): HTMLElement[] => {
    const result: HTMLElement[] = [];
    if (searchInputRef.current) {
      result.push(searchInputRef.current);
    }
    if (portalRef.current) {
      const treeItems = portalRef.current.querySelectorAll<HTMLElement>(
        "[data-tree-focusable]"
      );
      result.push(...Array.from(treeItems));
    }
    return result;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      parentRef.current?.focus();
      onClose();
      return;
    }

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const focusableItems = getFocusableItems();
      if (!focusableItems.length) return;

      const currentIndex = focusableItems.findIndex(
        (el) => el === document.activeElement
      );

      let nextIndex: number;
      if (e.key === "ArrowDown") {
        nextIndex =
          currentIndex < focusableItems.length - 1
            ? currentIndex + 1
            : currentIndex;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : 0;
      }

      const target = focusableItems[nextIndex];
      target?.focus();
      target?.scrollIntoView({ block: "nearest" });
    }
  };

  return (
    <EventOutside
      onEventOutside={() => {
        parentRef.current?.focus();
        onClose();
      }}
      parentElem={parentRef.current}
    >
      <div
        ref={portalRef}
        className={styles.portalWrapper}
        style={{
          top: coords.top,
          left: coords.left,
          width: bodyWidth,
          height: isLoading ? bodyHeight + 64 : bodyHeight,
        }}
        data-cy="organisation-filter-popup"
        onKeyDown={handleKeyDown}
      >
        <TextInput
          inputRef={searchInputRef}
          label={t("Common:organisation_search")}
          hideLabel
          autoFocus
          dataCy="organisation-filter-search"
          className={styles.treeSearch}
          renderIcon={() => <Icon name="search" size={20} />}
          value={localSearchValue}
          onChange={handleInputChange}
        />
        <div
          style={{
            height:
              isLoading && items.length === 0
                ? bodyHeight + 20
                : bodyHeight - 44,
          }}
          className={classNames({
            [styles.resultWrapper]: true,
          })}
          ref={resultWrapperRef}
        >
          {items.length === 0 && isLoading && <LoadingSpinner />}
          {items.map((i) => (
            <LiItem
              key={i.key}
              item={i}
              searchInflicted={!!searchValueRef.current.length}
            />
          ))}
        </div>
        {items.length !== 0 && isLoading && <LoadingSpinner />}
      </div>
    </EventOutside>
  );
};
